import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  greenStatuses, redStatuses, StatusDetailEnum, StatusEnum,
} from 'data/enums';
import { isIP } from 'is-ip';

/**
 * Throws error for a statement that should not be able to be executed.
 * https://tinytip.co/tips/ts-switch-assert-unreachable/
 * @param _value
 */
export const assertUnreachable = (_value: any): never => {
  throw new Error(`Statement should be unreachable: '${_value}'`);
};

/**
 *
 * @param query
 * @param terms
 * @returns true if any of the terms includes the query provided
 */
export function containsQuery(query: string, ...terms: string[]): boolean {
  return terms.some((term) => term.toLowerCase().includes(query.toLowerCase()));
}

// A custom hook that builds on useLocation to parse
// the query string for you.
export function useLocationQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 *
 * @param email string
 * @returns domain from email or null if not found
 */
export function getDomainFromEmail(email: string): string | null {
  const regex = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
  const match = email.match(regex);

  if (match && match[1]) {
    return match[1];
  }
  return null;
}

/**
 * categorizeStatus turns a detailed status into a StatusEnum.
 * @param status string that parses into a StatusDetailEnum
 * @returns StatusEnum
 */
export function categorizeStatus(status: string): StatusEnum {
  const StatusDetail = StatusDetailEnum[status as keyof typeof StatusDetailEnum];
  if (greenStatuses.has(StatusDetail)) {
    return StatusEnum.SUCCESS;
  }
  if (redStatuses.has(StatusDetail)) {
    return StatusEnum.FAILURE;
  }

  // If we haven't seen this status before, we assume it's unknown.
  return StatusEnum.UNKNOWN;
}

/**
 * formatDuration takes a duration in milliseconds and returns a string
 * representing the duration in milliseconds, seconds or minutes.
 * @param durationMS number
 * @returns string
 */
export function formatDuration(durationMS: number): string {
  if (durationMS < 1000) {
    return `${durationMS}ms`;
  }
  if (durationMS < 60000) {
    return `${Math.floor(durationMS / 1000)}s`;
  }
  return `${Math.floor(durationMS / 60000)} min`;
}

/**
 * toTitleCase takes a string and returns it in title case, i.e. it capitalizes the first
 * letter of each word. A word is defined as a sequence of characters separated by a space,
 * hyphen, or an underscore.
 * For example, all the following strings will be converted to "Hello World":
 * "hello world", "hello-world", "hello_world" and "HELLO WORLD"
 * @param str string
 * @returns string
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(/[\s-_]+/) // Split on space, hyphen, and underscore
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' '); // Join with a space to ensure title case
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const VALIDATION_REGEX = {
  alphaNumericAndSpaces: /^[\w\-\s]+$/,
  alphaNumericAndDashes: /^[a-zA-Z0-9-]+$/,
  alphaNumericAndSpacesDashesPeriods: /^[a-zA-Z0-9\s.-]+$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  url: /^(https):\/\/[^ "]+$/,
  reservedProjectNamePrefix: /^(amp-|ampersand)/,
  awsSecretKey: /^[A-Za-z0-9+/=]{40}$/,
  s3Bucket: /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/,
};

export const VALIDATION_ERROR_MESSAGES = {
  PROJECT_NAME_PREFIX: "Project name cannot start with 'amp-' or 'ampersand' as these are reserved",
  TOPIC_NAME_UNIQUE: "Topic name already exists in this project",
  DESTINATION_NAME_UNIQUE: "Destination name already exists in this project",
  LABEL_MAX_LENGTH: (maxLength: number) => `Label cannot exceed ${maxLength} characters`,
};

export const NO_RESERVED_PREFIX = 'noReservedPrefix';

export const urlPathWithSearchParams = (path: string, searchParams?: string) => {
  return path + (searchParams ? `?${searchParams}` : '');
};

// This function is used to validate a URL. It checks if the host is an IP address or if it contains a dot.
export const isValidURL = (s: URL): boolean => {
  if (isIP(s.host)) {
    return true;
  }

  if (!s.host.includes('.')) {
    return false;
  }

  return true;
};

/**
 * Formats a date consistently across the application.
 * Returns date in format: "MMM DD, YYYY HH:MM:SS" in UTC timezone
 * @param date Date object or null/undefined
 * @returns Formatted date string or "N/A" if date is null/undefined
 */
export function formatDateTime(date: Date | null | undefined): string {
  if (!date) return 'N/A';

  return date.toLocaleString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Formats a date to just the date portion (no time).
 * Returns date in format: "MMM DD, YYYY"
 * @param date Date object or null/undefined
 * @returns Formatted date string or "N/A" if date is null/undefined
 */
export function formatDate(date: Date | null | undefined): string {
  if (!date) return 'N/A';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Display labels for action types whose raw value does not title-case into something
 * user-facing. 
 */
const ACTION_TYPE_LABELS: Record<string, string> = {
  write: 'Write',
  'write.batch': 'Write',
  'subscribe.subscription.created': 'Subscription Created',
  'subscribe.subscription.deleted': 'Subscription Deleted',
  'subscribe.subscription.updated': 'Subscription Updated',
  'subscribe.subscription.refreshed': 'Subscription Refreshed',
};

/**
 * Formats action type for display in the UI.
 * Action types in ACTION_TYPE_LABELS use their explicit label (e.g. both "write" and
 * "write.batch" display as "Write"); everything else falls back to title case.
 * Replaces dots with spaces for better readability (e.g., "schema.watch" becomes "Schema Watch").
 * @param actionType string - the action type from the API (e.g., "write", "write.batch", "read", "schema.watch")
 * @returns string - formatted action type for display
 */
export function formatActionType(actionType: string): string {
  const label = ACTION_TYPE_LABELS[actionType];
  if (label) {
    return label;
  }
  // Replace dots with spaces before applying title case
  return toTitleCase(actionType.replace(/\./g, ' '));
}
